import React, { useState } from 'react';
import {
  Card,
  Stack,
  Group,
  Text,
  TextInput,
  PasswordInput,
  Select,
  Button,
  Collapse,
} from '@mantine/core';
import {
  IconKey,
  IconInfoCircle,
  IconCheck,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { STSClient, GetCallerIdentityCommand } from '@aws-sdk/client-sts';

/**
 * Shared credential change section for AWS pages
 * Provides collapsible UI for testing and updating AWS credentials
 *
 * @param {Object} awsCredentials - Current AWS credentials from context
 * @param {Function} setAwsCredentials - Function to update AWS credentials in context
 * @param {Function} onCredentialsUpdated - Optional callback fired after successful credential update
 */
const AWSCredentialChangeSection = ({
  awsCredentials,
  setAwsCredentials,
  onCredentialsUpdated
}) => {
  // Local state for credential editor
  const [showCredentialEditor, setShowCredentialEditor] = useState(false);
  const [credentialFormData, setCredentialFormData] = useState({
    accessKeyId: '',
    secretAccessKey: '',
    sessionToken: '',
    region: awsCredentials?.region || 'us-east-1'
  });
  const [credentialTesting, setCredentialTesting] = useState(false);

  // Form change handler
  const handleCredentialFormChange = (field) => (event) => {
    setCredentialFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  // Test credentials with AWS STS
  const handleTestCredentials = async () => {
    if (!credentialFormData.accessKeyId.trim() || !credentialFormData.secretAccessKey.trim()) {
      notifications.show({
        title: 'Validation Error',
        message: 'Access Key ID and Secret Access Key are required',
        color: 'red',
      });
      return;
    }

    setCredentialTesting(true);

    try {
      const stsClient = new STSClient({
        region: credentialFormData.region,
        credentials: {
          accessKeyId: credentialFormData.accessKeyId,
          secretAccessKey: credentialFormData.secretAccessKey,
          sessionToken: credentialFormData.sessionToken || undefined,
        },
      });

      const command = new GetCallerIdentityCommand({});
      const response = await stsClient.send(command);

      notifications.show({
        title: 'Test Successful',
        message: `Authenticated as: ${response.Arn}`,
        color: 'green',
      });
    } catch (error) {
      console.error(error);
      notifications.show({
        title: 'Test Failed',
        message: error.message || 'Failed to validate AWS credentials',
        color: 'red',
      });
    } finally {
      setCredentialTesting(false);
    }
  };

  // Update credentials in context after validation
  const handleUpdateCredentials = async () => {
    if (!credentialFormData.accessKeyId.trim() || !credentialFormData.secretAccessKey.trim()) {
      notifications.show({
        title: 'Validation Error',
        message: 'Access Key ID and Secret Access Key are required',
        color: 'red',
      });
      return;
    }

    setCredentialTesting(true);

    try {
      // Test credentials first
      const stsClient = new STSClient({
        region: credentialFormData.region,
        credentials: {
          accessKeyId: credentialFormData.accessKeyId,
          secretAccessKey: credentialFormData.secretAccessKey,
          sessionToken: credentialFormData.sessionToken || undefined,
        },
      });

      const command = new GetCallerIdentityCommand({});
      const response = await stsClient.send(command);

      // Update credentials in context
      setAwsCredentials({ ...credentialFormData });

      // Close the credential editor
      setShowCredentialEditor(false);

      notifications.show({
        title: 'Credentials Updated',
        message: `Successfully updated AWS credentials. Authenticated as: ${response.Arn}`,
        color: 'green',
      });

      // Call optional callback for parent to reset state
      if (onCredentialsUpdated) {
        onCredentialsUpdated();
      }

    } catch (error) {
      console.error(error);
      notifications.show({
        title: 'Update Failed',
        message: error.message || 'Failed to validate AWS credentials',
        color: 'red',
      });
    } finally {
      setCredentialTesting(false);
    }
  };

  // Toggle credential editor and pre-populate form
  const handleToggleCredentialEditor = () => {
    if (!showCredentialEditor) {
      // Pre-populate form with current credentials when opening
      setCredentialFormData({
        accessKeyId: awsCredentials?.accessKeyId || '',
        secretAccessKey: awsCredentials?.secretAccessKey || '',
        sessionToken: awsCredentials?.sessionToken || '',
        region: awsCredentials?.region || 'us-east-1'
      });
    }
    setShowCredentialEditor(!showCredentialEditor);
  };

  return (
    <>
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          Current Region: <strong>{awsCredentials?.region || 'Not set'}</strong>
        </Text>
        <Button
          variant="outline"
          leftSection={<IconKey size={16} />}
          onClick={handleToggleCredentialEditor}
          size="sm"
        >
          Change AWS Credentials
        </Button>
      </Group>

      {/* AWS Credential Switching Section */}
      <Collapse in={showCredentialEditor}>
        <Card withBorder>
          <Stack gap="md">
            <Group justify="space-between">
              <Text fw={500} size="sm">
                <IconKey size={16} style={{ marginRight: 8 }} />
                Update AWS Credentials
              </Text>
              <Text size="xs" c="dimmed">
                Current: {awsCredentials?.accessKeyId ? `${awsCredentials.accessKeyId.substring(0, 8)}...` : 'None'}
              </Text>
            </Group>

            <Stack gap="sm">
              <TextInput
                label="Access Key ID"
                placeholder="AKIA..."
                value={credentialFormData.accessKeyId}
                onChange={handleCredentialFormChange('accessKeyId')}
                leftSection={<IconKey size={16} />}
                required
              />

              <PasswordInput
                label="Secret Access Key"
                placeholder="Enter secret access key"
                value={credentialFormData.secretAccessKey}
                onChange={handleCredentialFormChange('secretAccessKey')}
                leftSection={<IconKey size={16} />}
                required
              />

              <PasswordInput
                label="Session Token (Optional)"
                placeholder="Enter session token if using temporary credentials"
                value={credentialFormData.sessionToken}
                onChange={handleCredentialFormChange('sessionToken')}
              />

              <Select
                label="Region"
                value={credentialFormData.region}
                onChange={(value) => setCredentialFormData(prev => ({ ...prev, region: value }))}
                data={[
                  { value: 'us-east-1', label: 'US East (N. Virginia)' },
                  { value: 'us-west-2', label: 'US West (Oregon)' },
                  { value: 'eu-west-1', label: 'Europe (Ireland)' },
                  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
                  { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' },
                  { value: 'ca-central-1', label: 'Canada (Central)' },
                  { value: 'eu-central-1', label: 'Europe (Frankfurt)' },
                  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
                  { value: 'us-west-1', label: 'US West (N. California)' },
                  { value: 'eu-west-2', label: 'Europe (London)' },
                  { value: 'ap-south-1', label: 'Asia Pacific (Mumbai)' },
                  { value: 'sa-east-1', label: 'South America (São Paulo)' },
                ]}
                required
              />
            </Stack>

            <Group justify="flex-end">
              <Button
                variant="outline"
                onClick={handleTestCredentials}
                loading={credentialTesting}
                leftSection={<IconInfoCircle size={16} />}
              >
                Test Connection
              </Button>
              <Button
                onClick={handleUpdateCredentials}
                loading={credentialTesting}
                leftSection={<IconCheck size={16} />}
              >
                Update Credentials
              </Button>
            </Group>
          </Stack>
        </Card>
      </Collapse>
    </>
  );
};

export default AWSCredentialChangeSection;
